import { styled } from "goober";
import { useEffect, useState } from "react";
import { queries } from "../../shared/queries";
import { useQuery } from "../hooks/useQuery";
import { isSpaceMember, useSpaces } from "../hooks/useSpaces";
import { useWorkflowZipImport } from "../hooks/useWorkflowZipImport";
import { Button } from "./Button";
import { Modal } from "./Modal";
import { SpaceDropdown } from "./SpaceDropdown";
import { Text } from "./Text";

export function ImportWorkflowModal({
  file,
  defaultSpaceId,
  onClose,
}: {
  file: File;
  defaultSpaceId?: string;
  onClose: () => void;
}) {
  const [spaces] = useSpaces({ settingsView: false });
  const [personalSpace] = useQuery(queries.personalSpace());
  const [preferences] = useQuery(queries.userPreferences());
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);
  const { importError, isImporting, importWorkflowZip } =
    useWorkflowZipImport(selectedSpaceId);

  useEffect(() => {
    if (selectedSpaceId) return;
    const members = spaces.filter(isSpaceMember);
    if (members.length === 0) return;
    const memberId = (id: string | null | undefined) =>
      members.find((s) => s.id === id)?.id;
    setSelectedSpaceId(
      memberId(defaultSpaceId) ??
        memberId(preferences?.defaultSpace) ??
        memberId(personalSpace?.id) ??
        members[0]!.id
    );
  }, [
    selectedSpaceId,
    spaces,
    defaultSpaceId,
    preferences?.defaultSpace,
    personalSpace?.id,
  ]);

  return (
    <Modal open onClose={onClose} alert>
      <Content>
        <TitleBlock>
          <Text variant="text-regular-md" color="var(--text-primary)">
            Import workflow
          </Text>
          <Text variant="text-regular" color="var(--text-secondary)">
            Create a new workflow from “{file.name}” in the space you choose.
          </Text>
        </TitleBlock>
        <Field>
          <Text variant="text-small-md" color="var(--text-secondary)">
            Space
          </Text>
          <SpaceDropdown
            selectedSpaceId={selectedSpaceId}
            onSelect={setSelectedSpaceId}
            membersOnly
            variant="field"
          />
        </Field>
        {importError && (
          <Text variant="text-small" color="var(--text-error)">
            {importError}
          </Text>
        )}
        <Footer>
          <Button size="l" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="l"
            disabled={!selectedSpaceId || isImporting}
            onClick={async () => {
              if (await importWorkflowZip(file)) onClose();
            }}
          >
            Create workflow
          </Button>
        </Footer>
      </Content>
    </Modal>
  );
}

const Content = styled("div")`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 320px;
`;

const TitleBlock = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Field = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Footer = styled("div")`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;
